import React, { useState } from "react";
import { FootballAnalyticsView } from "../football-intelligence/AnalyticsView";
import { PassingNetwork, Unavailable } from "../football-intelligence/FootballPitch";
import HeatmapExplorer from "../football-intelligence/HeatmapExplorer";
import NetworkExplorer from "../football-intelligence/NetworkExplorer";
import XgTimeline from "../football-intelligence/XgTimeline";
import { ActualsComparison, ShotComparison } from "../football-intelligence/MatchPanels";
import { Segmented } from "./ScrollTabs";
import "../football-intelligence/football-intelligence.css";
import "../football-intelligence/fi-extra.css";
import "./football-app.css";

const PROVENANCE = "PitchAPI · historical actuals, not live tracking · recorded provider data.";

/**
 * Advanced match analysis, admin-gated upstream. Each section renders only
 * what the cached bundle actually holds for this fixture and otherwise says
 * so; nothing is estimated or interpolated.
 */
export default function MatchAnalysis({ data }) {
  const [view, setView] = useState("xg");
  const [side, setSide] = useState("home");
  if (!data || data.status !== "AVAILABLE") {
    return <div className="fa-empty">Advanced match data is unavailable: no verified provider mapping or cached fixture.</div>;
  }
  const profile = data.profile || {};
  const spatial = data.spatial || {};
  const home = profile.home_team;
  const away = profile.away_team;
  const teams = data.teams || {};
  const actuals = data.actuals || {};
  const timeline = data.xg_timeline || [];
  const shots = spatial.shots || [];
  const nameOf = () => profile.name;
  const heat = (t) => {
    const h = spatial.heatmaps?.teams?.find((x) => x.team?.id === t?.id);
    return h ? [{ ...h, grid: spatial.heatmaps.grid, fixture_id: profile.id }] : [];
  };
  const net = (t) => {
    const n = spatial.networks?.find((x) => x.team?.id === t?.id);
    return n ? [{ ...n, fixture_id: profile.id }] : [];
  };
  const heatSides = [[home, heat(home)], [away, heat(away)]].filter(([, h]) => h.length);
  const netSides = [[home, net(home)], [away, net(away)]].filter(([, n]) => n.length);
  const hasActuals = !!(actuals.home || actuals.away);
  const options = [
    { id: "xg", label: "xG & shots" },
    { id: "territory", label: "Territory" },
    { id: "passing", label: "Passing" },
    { id: "team", label: "Team detail" },
  ];
  const team = teams[side];

  return (
    <div className="fi-shell fi-embedded" style={{ background: "none", border: 0, boxShadow: "none" }}>
      <div className="fa-warn" role="note" style={{ marginTop: 12 }}>
        Source: PitchAPI recorded match analytics. Check the source shown on the Stats tab when comparing figures; provider definitions can differ.
      </div>
      {data.coverage && <p className="fa-note">{data.coverage.status === 'COMPLETE_CACHED_DATASETS' ? 'Complete cached datasets' : 'Partial cached datasets'} · recorded match, not a live feed.{!!data.coverage.missing?.length && ` Missing: ${data.coverage.missing.map(x => x.replaceAll('_', ' ')).join(', ')}.`}</p>}
      <Segmented options={options} value={view} onChange={setView} label="Analysis views" />
      {view === "xg" && (
        <>
          {hasActuals && <div className="fa-card"><h3>Match numbers</h3><ActualsComparison headlineOnly actuals={actuals} homeName={home?.name} awayName={away?.name} /></div>}
          <div className="fa-card"><h3>Expected goals timeline</h3><XgTimeline timeline={timeline} shots={shots} home={home} away={away} /></div>
          <div className="fa-card"><h3>Chance quality &amp; shot map</h3><ShotComparison shots={shots} home={home} away={away} /></div>
        </>
      )}
      {view === "territory" && (
        <div className="fa-card">
          <h3>Territory &amp; field tilt</h3>
          {hasActuals && <ActualsComparison actuals={actuals} homeName={home?.name} awayName={away?.name} />}
          <h3 style={{ marginTop: 16 }}>Observed action density</h3>
          {heatSides.length ? (
            <div className="fx-pair">
              {heatSides.map(([t, h]) => (
                <div key={t?.id}><h4 style={{ margin: "0 0 8px", fontSize: 13 }}>{t?.name}</h4><HeatmapExplorer hideCoverage heatmaps={h} matchName={nameOf} sampleSize={1} /></div>
              ))}
            </div>
          ) : (
            <Unavailable text="No verified action-density grid is cached for this match." />
          )}
        </div>
      )}
      {view === "passing" && (
        <div className="fa-card">
          <h3>Passing networks</h3>
          {netSides.length ? (
            <div className="fx-stack">
              {netSides.map(([t, n]) => (
                <div key={t?.id}><h4 style={{ margin: "0 0 8px", fontSize: 13 }}>{t?.name}</h4><NetworkExplorer hideCoverage networks={n} matchName={nameOf} sampleSize={1} /></div>
              ))}
              {netSides.length === 1 && <p className="fi-note">The other team has no passing network in the cached data for this match.</p>}
            </div>
          ) : (
            <PassingNetwork network={null} />
          )}
        </div>
      )}
      {view === "team" && (teams.home || teams.away) && (
        <div className="fa-card">
          <Segmented options={[{ id: "home", label: home?.name || "Home" }, { id: "away", label: away?.name || "Away" }]} value={side} onChange={setSide} label="Team" />
          {team ? <FootballAnalyticsView data={team} kind="team" embedded /> : <Unavailable text="No cached analytics for this team." />}
        </div>
      )}
      <p className="fa-note">{PROVENANCE}</p>
    </div>
  );
}
